import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { useRegisterCmdKCommands } from '../../contexts/CmdKContext';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  ShoppingCart, Search, Filter, Package, AlertTriangle,
  ArrowRight, Loader2, FolderOpen, ChevronDown, ChevronRight,
  XCircle, RotateCcw, Eye, EyeOff, Send, Printer, FileSpreadsheet,
  ArrowUp, ArrowDown, ArrowUpDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { TableSkeleton } from '../../lib/skeletons';
import { SearchableCombobox } from '../../components/ui/searchable-combobox';
import { exportToCSV, printTable, buildReport, triggerPrint } from '../../lib/printUtils';

const fmt = (n) => Math.round(n || 0).toLocaleString('es-CL');

const MOTIVOS = [
  { value: 'oc_generada', label: 'OC ya generada' },
  { value: 'solucionado_internamente', label: 'Solucionado internamente' },
  { value: 'no_se_requiere', label: 'No se requiere' },
  { value: 'otro', label: 'Otro' },
];

function PrioridadBadge({ prioridad }) {
  if (prioridad === 'muy_urgente') return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-200 text-red-800">MUY URG</span>;
  if (prioridad === 'urgente') return <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-orange-100 text-orange-700">Urgente</span>;
  return null;
}

export default function PendientesCompra() {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [stats, setStats] = useState({ total: 0, ots_afectadas: 0, por_familia: {} });
  const [loading, setLoading] = useState(false);

  const [filterOt, setFilterOt] = useState('');
  const [filterFamilia, setFilterFamilia] = useState('');
  const [filterOrigen, setFilterOrigen] = useState('web'); // 'web' | 'access' | 'todas'
  const [search, setSearch] = useState('');
  const searchTimer = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [mostrarBajas, setMostrarBajas] = useState(false);

  const [selected, setSelected] = useState(new Set());
  // Iter 339 (Renzo): por defecto agrupado por Familia
  const [groupBy, setGroupBy] = useState('familia');
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 200;

  // Dar de baja dialog
  const [bajaOpen, setBajaOpen] = useState(false);
  const [bajaMotivo, setBajaMotivo] = useState('oc_generada');
  const [bajaObs, setBajaObs] = useState('');
  const [bajaProcesando, setBajaProcesando] = useState(false);

  const loadData = useCallback(async (currentPage = 0) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterOt) params.append('ot', filterOt);
      if (filterFamilia) params.append('familia', filterFamilia);
      if (debouncedSearch) params.append('q', debouncedSearch);
      params.append('skip', String(currentPage * PAGE_SIZE));
      params.append('limit', String(PAGE_SIZE));
      if (mostrarBajas) params.append('mostrar_dados_de_baja', 'true');
      params.append('source', filterOrigen);
      const res = await api.get(`/compras/items-pendientes-compra?${params}`);
      setData(res.data.items || []);
      setStats({ total: res.data.total, ots_afectadas: res.data.ots_afectadas, por_familia: res.data.por_familia || {} });
      setSelected(new Set());
    } catch {
      toast.error('Error cargando items pendientes');
    } finally {
      setLoading(false);
    }
  }, [filterOt, filterFamilia, debouncedSearch, mostrarBajas, filterOrigen]);

  useEffect(() => { setPage(0); loadData(0); }, [loadData]);

  // Carlos (10/05/2026): imprimir y exportar Pendientes de Compra (lo pidió específicamente)
  const imprimirPendientes = () => {
    if (!data.length) { toast.warning('No hay pendientes para imprimir'); return; }
    printTable({
      title: 'Pedidos / Pendientes de Compra',
      subtitle: `${data.length} items · ${filterOrigen === 'web' ? 'Solo Web' : filterOrigen === 'access' ? 'Solo Access' : 'Todos'} · Página ${page + 1} · ${new Date().toLocaleString('es-CL')}`,
      summary: [
        { label: 'Total pendientes', value: stats.total },
        { label: 'OTs afectadas', value: stats.ots_afectadas },
        { label: 'Familias distintas', value: Object.keys(stats.por_familia || {}).length },
        { label: 'Búsqueda', value: debouncedSearch || filterOt || filterFamilia || 'Todos' },
      ],
      columns: [
        { key: 'origen', label: 'Origen', align: 'center', width: '50px', format: (v) => (v || '').toUpperCase() },
        { key: 'requisicion', label: 'Req/Pedido', mono: true, align: 'center' },
        { key: 'fecha', label: 'Fecha' },
        { key: 'ot', label: 'OT', align: 'center' },
        { key: 'codigo', label: 'Código', mono: true, format: (v, r) => v || r.material_codigo || '' },
        { key: 'descripcion', label: 'Descripción' },
        { key: 'familia', label: 'Familia' },
        { key: 'cantidad', label: 'Cant.', align: 'right', mono: true, format: (v) => fmt(v) },
        { key: 'unidad', label: 'Unidad', align: 'center' },
        { key: 'solicitante', label: 'Solicitante' },
        { key: 'prioridad', label: 'Prioridad', align: 'center', format: (v) => (v || 'normal').toUpperCase() },
      ],
      rows: data,
    });
  };

  // Iter 306 (Carlos): resumen compacto imprimible agrupado por Familia u OT
  const imprimirResumen = async (por) => {
    try {
      const params = new URLSearchParams();
      if (filterOt) params.append('ot', filterOt);
      if (filterFamilia) params.append('familia', filterFamilia);
      if (debouncedSearch) params.append('q', debouncedSearch);
      params.append('limit', '5000');
      params.append('source', filterOrigen);
      const res = await api.get(`/compras/items-pendientes-compra?${params}`);
      const items = res.data.items || [];
      if (!items.length) { toast.warning('No hay pendientes para el resumen'); return; }

      const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      const groups = {};
      for (const it of items) {
        const key = por === 'ot' ? (it.ot_numero ? `OT ${it.ot_numero}` : 'Sin OT') : (it.concepto || 'Sin Familia');
        (groups[key] = groups[key] || []).push(it);
      }
      const keys = Object.keys(groups).sort((a, b) => a.localeCompare(b, 'es', { numeric: true }));

      let tableHTML = '';
      for (const key of keys) {
        const rowsHTML = groups[key].map(it => `
          <tr>
            <td style="text-align:center;font-family:monospace;">${esc(it.origen === 'access' ? 'ACC' : 'WEB')}-${esc(it.requisicion)}</td>
            <td>${esc(it.descripcion)}</td>
            <td style="text-align:right;font-family:monospace;">${fmt(it.cantidad_pendiente)}</td>
            <td style="text-align:center;">${esc(it.unidad)}</td>
            <td style="text-align:center;">${por === 'ot' ? esc(it.concepto || '-') : esc(it.ot_numero || '-')}</td>
            <td>${esc(it.solicitante)}</td>
            <td style="text-align:center;">${esc(it.fecha || '')}</td>
          </tr>`).join('');
        tableHTML += `
          <div class="grupo-titulo">${esc(key)} · ${groups[key].length} ítem(s)</div>
          <table>
            <thead><tr>
              <th style="width:62px;text-align:center;">Req</th><th>Descripción</th>
              <th style="width:42px;text-align:right;">Cant.</th><th style="width:32px;text-align:center;">Un.</th>
              <th style="width:82px;text-align:center;">${por === 'ot' ? 'Familia' : 'OT'}</th>
              <th style="width:88px;">Solicitante</th><th style="width:56px;text-align:center;">Fecha</th>
            </tr></thead>
            <tbody>${rowsHTML}</tbody>
          </table>`;
      }

      const html = buildReport({
        title: `Resumen Pendientes de Compra por ${por === 'ot' ? 'OT' : 'Familia'}`,
        subtitle: `${items.length} ítems · ${keys.length} grupos · ${new Date().toLocaleString('es-CL')}`,
        summaryBoxes: [
          { label: 'Ítems pendientes', value: items.length },
          { label: por === 'ot' ? 'OTs' : 'Familias', value: keys.length },
          { label: 'Origen', value: (filterOrigen || 'web').toUpperCase() },
        ],
        tableHTML,
        fontScale: 0.85,
        extraStyles: `
          th, td { padding: 1px 3px !important; line-height: 1.15; }
          table { margin-bottom: 4px; page-break-inside: auto; }
          tr { page-break-inside: avoid; }
          .grupo-titulo { font-size: 8.5px; font-weight: 700; background: #edf2f7; border-left: 3px solid #003366;
                          padding: 2px 5px; margin: 5px 0 2px 0; text-transform: uppercase; page-break-after: avoid; }
        `,
      });
      triggerPrint(html);
    } catch {
      toast.error('Error generando el resumen');
    }
  };

  const exportarPendientesExcel = () => {
    if (!data.length) { toast.warning('No hay pendientes para exportar'); return; }
    exportToCSV({
      filename: `pedidos_pendientes_compra_pag${page + 1}`,
      title: 'CHIRINO STEEL · Pedidos / Pendientes de Compra',
      summary: [
        { label: 'Total pendientes (sistema)', value: stats.total },
        { label: 'OTs afectadas', value: stats.ots_afectadas },
        { label: 'Items en archivo', value: data.length },
        { label: 'Página', value: page + 1 },
        { label: 'Filtro origen', value: filterOrigen },
        { label: 'Filtro OT', value: filterOt || 'Todas' },
        { label: 'Filtro familia', value: filterFamilia || 'Todas' },
        { label: 'Búsqueda', value: debouncedSearch || '-' },
        { label: 'Generado', value: new Date().toLocaleString('es-CL') },
      ],
      columns: [
        { key: 'origen', label: 'Origen', format: (v) => (v || '').toUpperCase() },
        { key: 'requisicion', label: 'Requisición/Pedido' },
        { key: 'fecha', label: 'Fecha' },
        { key: 'ot', label: 'OT' },
        { key: 'codigo', label: 'Código', format: (v, r) => v || r.material_codigo || '' },
        { key: 'descripcion', label: 'Descripción' },
        { key: 'familia', label: 'Familia' },
        { key: 'cantidad', label: 'Cantidad', format: (v) => Number(v) || 0 },
        { key: 'unidad', label: 'Unidad' },
        { key: 'solicitante', label: 'Solicitante' },
        { key: 'prioridad', label: 'Prioridad', format: (v) => (v || 'normal').toUpperCase() },
        { key: 'observaciones', label: 'Observaciones' },
        { key: 'estado', label: 'Estado' },
        { key: 'fecha_baja', label: 'Fecha baja', format: (v) => v || '' },
        { key: 'motivo_baja', label: 'Motivo baja' },
      ],
      rows: data,
    });
  };

  const handleSearch = (val) => {
    setSearch(val);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setDebouncedSearch(val), 400);
  };

  const familias = useMemo(() => Object.keys(stats.por_familia).sort(), [stats.por_familia]);

  const itemKey = (it, idx) => `${it.origen}-${it.requisicion}-${it.item_num}-${idx}`;

  const toggleSelect = (key) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === data.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(data.map((it, idx) => itemKey(it, idx))));
    }
  };

  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      next.has(groupKey) ? next.delete(groupKey) : next.add(groupKey);
      return next;
    });
  };

  const getSelectedItems = () => {
    return data
      .map((it, idx) => ({ ...it, _idx: idx }))
      .filter((it) => selected.has(itemKey(it, it._idx)));
  };

  const goToGenerarOC = () => {
    const selItems = getSelectedItems().filter(it => !it.dado_de_baja);
    if (selItems.length === 0) { toast.error('Seleccione items activos primero'); return; }
    const itemsForOC = selItems.map(it => ({
      descripcion: it.descripcion,
      cantidad: it.cantidad_pendiente,
      unidad: it.unidad,
      codigo_material: it.codigo,
      ot_numero: it.ot_numero,
      cuenta_gasto: it.cuenta_gasto,
      subfamilia_codpf: it.subfamilia_codpf || '',
      observaciones: `Pedido #${it.requisicion} (${it.origen})`,
      requisicion_numero: it.origen === 'web' ? parseInt(it.requisicion) || null : null,
      pedido_origen: it.origen,
      pedido_numero_origen: it.requisicion,
      pedido_item_origen: it.item_num,
    })).filter(i => i.cantidad > 0);

    if (itemsForOC.length === 0) { toast.error('Los items seleccionados no tienen cantidad pendiente'); return; }
    sessionStorage.setItem('oc_items_from_pedidos', JSON.stringify(itemsForOC));
    const solicitantes = [...new Set(selItems.map(i => i.solicitante).filter(Boolean))];
    sessionStorage.setItem('oc_meta_from_pedidos', JSON.stringify({ solicitante: solicitantes.join(', ') }));
    navigate('/compras/generar-oc');
  };

  const goToSolicitarCotizacion = () => {
    const selItems = getSelectedItems().filter(it => !it.dado_de_baja);
    if (selItems.length === 0) { toast.error('Seleccione items activos primero'); return; }
    const itemsParam = selItems.map(it => ({
      descripcion: it.descripcion,
      cantidad_pendiente: it.cantidad_pendiente,
      unidad: it.unidad,
      codigo: it.codigo,
      ot_numero: it.ot_numero,
      requisicion: it.requisicion,
      familia: it.familia || it.familia_nombre || '',
      subfamilia_codpf: it.subfamilia_codpf || '',
      observacion: it.observacion || '',
    }));
    navigate(`/compras/solicitar-cotizacion?items=${encodeURIComponent(JSON.stringify(itemsParam))}`);
  };

  const openBajaDialog = () => {
    const selItems = getSelectedItems();
    if (selItems.length === 0) { toast.error('Seleccione items primero'); return; }
    setSingleBajaItem(null);
    setBajaMotivo('oc_generada');
    setBajaObs('');
    setBajaOpen(true);
  };

  const handleReactivar = async () => {
    const selItems = getSelectedItems().filter(it => it.dado_de_baja);
    if (selItems.length === 0) { toast.error('Seleccione items dados de baja para reactivar'); return; }
    try {
      const res = await api.post('/compras/items-pendientes-compra/reactivar', {
        items: selItems.map(it => ({ origen: it.origen, requisicion: it.requisicion, item_num: it.item_num })),
      });
      toast.success(res.data.message);
      loadData(page);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al reactivar');
    }
  };

  const handleReactivarSingle = async (it) => {
    try {
      const res = await api.post('/compras/items-pendientes-compra/reactivar', {
        items: [{ origen: it.origen, requisicion: it.requisicion, item_num: it.item_num }],
      });
      toast.success(res.data.message);
      loadData(page);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al reactivar');
    }
  };

  // Single-item quick baja (from row action)
  const [singleBajaItem, setSingleBajaItem] = useState(null);
  const openSingleBaja = (it) => {    setSingleBajaItem(it);
    setBajaMotivo('oc_generada');
    setBajaObs('');
    setBajaOpen(true);
  };

  const handleDarDeBajaConfirm = async () => {
    let itemsToProcess;
    if (singleBajaItem) {
      itemsToProcess = [{ origen: singleBajaItem.origen, requisicion: singleBajaItem.requisicion, item_num: singleBajaItem.item_num }];
    } else {
      const selItems = getSelectedItems().filter(it => !it.dado_de_baja);
      if (selItems.length === 0) { toast.error('No hay items activos para dar de baja'); setBajaOpen(false); return; }
      itemsToProcess = selItems.map(it => ({ origen: it.origen, requisicion: it.requisicion, item_num: it.item_num }));
    }
    setBajaProcesando(true);
    try {
      const res = await api.post('/compras/items-pendientes-compra/dar-de-baja', {
        items: itemsToProcess,
        motivo: bajaMotivo,
        observacion: bajaObs,
      });
      toast.success(res.data.message);
      setBajaOpen(false);
      setSingleBajaItem(null);
      loadData(page);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al dar de baja');
    } finally {
      setBajaProcesando(false);
    }
  };

  // Count active vs baja in selection
  const selActive = getSelectedItems().filter(it => !it.dado_de_baja).length;
  const selBaja = getSelectedItems().filter(it => it.dado_de_baja).length;

  // Orden por columna (asc/desc) — click en el título de la columna
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };
  const SORT_NUMERIC = new Set(['cantidad_solicitada', 'cantidad_comprada', 'cantidad_pendiente', 'ot_numero', 'requisicion']);
  const sortedData = useMemo(() => {
    const arr = data.map((it, idx) => ({ ...it, _idx: idx }));
    if (!sortCol) return arr;
    const dir = sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      const va = a[sortCol];
      const vb = b[sortCol];
      if (SORT_NUMERIC.has(sortCol)) {
        return ((parseFloat(va) || 0) - (parseFloat(vb) || 0)) * dir;
      }
      return String(va || '').localeCompare(String(vb || ''), 'es', { numeric: true }) * dir;
    });
    return arr;
  }, [data, sortCol, sortDir]); // eslint-disable-line react-hooks/exhaustive-deps

  // Grouped data
  const groupedData = useMemo(() => {
    if (groupBy === 'none') return null;
    const groups = {};
    sortedData.forEach((it) => {
      const key = groupBy === 'ot'
        ? `OT ${it.ot_numero || 'Sin OT'}`
        : groupBy === 'requisicion'
          ? `${it.origen === 'access' ? 'ACC' : 'WEB'}-${it.requisicion}`
          : (it.concepto || 'Sin Familia');
      if (!groups[key]) groups[key] = [];
      groups[key].push(it);
    });
    const entries = Object.entries(groups);
    if (groupBy === 'familia') {
      // Iter 339: alfabético, con "Sin Familia" primero para asociarlas de inmediato
      entries.sort((a, b) => {
        if (a[0] === 'Sin Familia') return -1;
        if (b[0] === 'Sin Familia') return 1;
        return a[0].localeCompare(b[0], 'es');
      });
    } else {
      entries.sort((a, b) => a[0].localeCompare(b[0], 'es', { numeric: true }));
    }
    return entries;
  }, [sortedData, groupBy]);

  // Iter 339: al cargar/cambiar agrupación, expandir todos los grupos
  useEffect(() => {
    if (groupedData) setExpandedGroups(new Set(groupedData.map(([k]) => k)));
     
  }, [data, groupBy]); // eslint-disable-line react-hooks/exhaustive-deps

  // Iter 339: asociar familia a material sin clasificar (inline) · Iter 359: modo lote (varios ítems → un material)
  const [asignarItem, setAsignarItem] = useState(null);
  const [asignarBatch, setAsignarBatch] = useState([]);
  const [asignarCodpf, setAsignarCodpf] = useState('');
  const [asignarLoading, setAsignarLoading] = useState(false);
  const [subfamilias, setSubfamilias] = useState([]);
  // Modo "vincular material" para ítems de texto libre (sin código)
  const [matSearch, setMatSearch] = useState('');
  const [matResults, setMatResults] = useState([]);
  const [matTotal, setMatTotal] = useState(0);
  const [matSelected, setMatSelected] = useState(null);
  const [matSearching, setMatSearching] = useState(false);
  const [subfamSolo, setSubfamSolo] = useState('');
  const esBatch = asignarBatch.length > 0;
  const modoVincular = esBatch || (asignarItem && !(asignarItem.codigo || asignarItem.material_codigo));
  const openAsignarFamilia = async (it, batch = []) => {
    setAsignarCodpf('');
    setSubfamSolo('');
    setMatSearch('');
    setMatResults([]);
    setMatTotal(0);
    setMatSelected(null);
    setAsignarBatch(batch);
    setAsignarItem(it);
    if (subfamilias.length === 0) {
      try {
        const res = await api.get('/bodega/subfamilias-lista');
        setSubfamilias(res.data.subfamilias || []);
      } catch {
        toast.error('No se pudo cargar la lista de subfamilias');
      }
    }
  };
  // Iter 360: el botón "Asociar" de una fila aplica a TODOS los seleccionados (más la fila clickeada)
  const openAsignarDesdeFila = (it, idx) => {
    const sel = getSelectedItems().filter((s) => !s.dado_de_baja && !(s.codigo || s.material_codigo));
    const key = itemKey(it, idx);
    const conFila = sel.some((s) => itemKey(s, s._idx) === key) ? sel : [{ ...it, _idx: idx }, ...sel];
    if (conFila.length > 1 && !(it.codigo || it.material_codigo)) openAsignarFamilia(it, conFila);
    else openAsignarFamilia(it);
  };
  const openAsignarBatch = () => {
    const items = getSelectedItems().filter((it) => !it.dado_de_baja);
    if (items.length === 0) return;
    openAsignarFamilia(items[0], items);
  };
  useEffect(() => {
    if (!asignarItem || !modoVincular) return undefined;
    if (!matSearch.trim()) { setMatResults([]); setMatTotal(0); return undefined; }
    const t = setTimeout(async () => {
      setMatSearching(true);
      try {
        const res = await api.get('/bodega/maestro-materiales', { params: { q: matSearch.trim(), limit: 40 } });
        setMatResults(res.data.materiales || []);
        setMatTotal(res.data.total || 0);
      } catch {
        setMatResults([]);
      } finally {
        setMatSearching(false);
      }
    }, 350);
    return () => clearTimeout(t);
     
  }, [matSearch, asignarItem, modoVincular]); // eslint-disable-line react-hooks/exhaustive-deps
  const cerrarAsignar = () => { setAsignarItem(null); setAsignarBatch([]); };
  const handleAsignarFamilia = async () => {
    const codigoExistente = asignarItem?.codigo || asignarItem?.material_codigo;
    const itemsObjetivo = esBatch ? asignarBatch : [asignarItem];
    const payloadItems = itemsObjetivo.map((it) => ({ origen: it.origen, requisicion: String(it.requisicion), item_num: it.item_num }));
    setAsignarLoading(true);
    try {
      if (modoVincular && !matSelected && subfamSolo) {
        // Iter 360: no existe el material exacto → clasificar solo por subfamilia
        const res = await api.post('/compras/items-pendientes-compra/asociar-subfamilia', { items: payloadItems, codpf: subfamSolo });
        if (res.data.errores?.length) toast.warning(`${res.data.message}: ${res.data.errores.slice(0, 3).join(' | ')}`);
        else toast.success(res.data.message);
      } else if (esBatch) {
        if (!matSelected) return;
        const res = await api.post('/compras/items-pendientes-compra/asociar-material-batch', {
          items: payloadItems,
          codigo_material: matSelected.codigo,
        });
        if (!matSelected.concepto && asignarCodpf) {
          await api.post(`/bodega/maestro-materiales/${encodeURIComponent(matSelected.codigo)}/asignar-familia`, { codpf: asignarCodpf });
        }
        if (res.data.errores?.length) toast.warning(`${res.data.message}: ${res.data.errores.slice(0, 3).join(' | ')}`);
        else toast.success(res.data.message);
      } else if (codigoExistente) {
        // Material ya vinculado pero sin familia → asignar familia al maestro
        const res = await api.post(`/bodega/maestro-materiales/${encodeURIComponent(codigoExistente)}/asignar-familia`, { codpf: asignarCodpf });
        toast.success(res.data.message);
      } else {
        // Ítem de texto libre → vincular al material seleccionado del maestro
        if (!matSelected) return;
        const res = await api.post('/compras/items-pendientes-compra/asociar-material', {
          origen: asignarItem.origen,
          requisicion: String(asignarItem.requisicion),
          item_num: asignarItem.item_num,
          codigo_material: matSelected.codigo,
        });
        // Si el material elegido tampoco tiene familia y se eligió subfamilia → asignarla también
        if (!matSelected.concepto && asignarCodpf) {
          await api.post(`/bodega/maestro-materiales/${encodeURIComponent(matSelected.codigo)}/asignar-familia`, { codpf: asignarCodpf });
        }
        toast.success(res.data.message);
      }
      cerrarAsignar();
      loadData(page);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al asociar');
    } finally {
      setAsignarLoading(false);
    }
  };

  const renderRow = (it, idx) => {
    const key = itemKey(it, idx);
    const isSelected = selected.has(key);
    const isBaja = it.dado_de_baja;
    return (
      <tr key={key} className={`border-b text-xs hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50' : ''} ${isBaja ? 'opacity-50 bg-gray-100' : ''}`}>
        <td className="p-2 text-center">
          <Checkbox checked={isSelected} onCheckedChange={() => toggleSelect(key)} data-testid={`check-${idx}`} />
        </td>
        <td className="p-2">
          {isBaja ? <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-gray-300 text-gray-700">BAJA</span> : <PrioridadBadge prioridad={it.prioridad} />}
        </td>
        <td className="p-2 font-mono text-[11px]">{it.origen === 'access' ? 'ACC' : 'WEB'}-{it.requisicion}</td>
        <td className="p-2 font-mono text-[11px] text-center whitespace-nowrap">{it.fecha ? String(it.fecha).slice(0, 10).split('-').reverse().join('-') : '-'}</td>
        <td className="p-2 font-mono">{it.ot_numero || '-'}</td>
        <td className="p-2 max-w-[300px]">
          <span className="block truncate" title={it.descripcion}>{it.descripcion}</span>
          {it.repuesto_por_anulacion_oc && (
            <span className="inline-flex items-center gap-1 mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-purple-100 text-purple-700 border border-purple-200"
              title={`Este ítem reapareció porque se anuló la OC ${it.repuesto_por_anulacion_oc}`}
              data-testid={`badge-repuesto-anulacion-${idx}`}>
              <RotateCcw className="w-3 h-3" /> Vuelve por anulación OC {it.repuesto_por_anulacion_oc}
            </span>
          )}
        </td>
        <td className="p-2 text-right font-medium">{fmt(it.cantidad_solicitada)}</td>
        <td className="p-2 text-right text-green-700">{fmt(it.cantidad_comprada)}</td>
        <td className="p-2 text-right font-bold text-orange-700">{fmt(it.cantidad_pendiente)}</td>
        <td className="p-2 text-center">{it.unidad}</td>
        <td className="p-2 text-muted-foreground max-w-[160px]" title={[it.concepto, it.familia_nombre, it.subfamilia_nombre].filter(Boolean).join(' › ')}>
          {it.concepto ? (
            <div className="leading-tight">
              <span className="block truncate">{it.concepto}</span>
              {(it.familia_nombre || it.subfamilia_nombre) && (
                <span className="block truncate text-[10px] text-gray-400" data-testid={`fam-sub-${idx}`}>
                  {[it.familia_nombre, it.subfamilia_nombre].filter(Boolean).join(' › ')}
                </span>
              )}
              {it.clasif_por_subfamilia && !isBaja && (
                <button onClick={() => openAsignarDesdeFila(it, idx)} className="text-[10px] text-amber-700 hover:underline" title="Clasificado solo por subfamilia (sin material del maestro). Clic para vincular un material." data-testid={`btn-vincular-material-${idx}`}>
                  solo subfamilia · vincular material
                </button>
              )}
            </div>
          ) : (
            !isBaja ? (
              <button
                onClick={() => openAsignarDesdeFila(it, idx)}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-300 hover:bg-amber-100 transition-colors"
                title={(it.codigo || it.material_codigo) ? 'Material sin familia — asociar familia aquí mismo' : 'Ítem de texto libre — buscar material del maestro (o subfamilia) y vincularlo. Si hay varios ítems seleccionados, se asocian todos.'}
                data-testid={`btn-asignar-familia-${idx}`}
              >
                <FolderOpen className="w-3 h-3" /> Asociar
              </button>
            ) : '-'
          )}
        </td>
        <td className="p-2 text-muted-foreground">{it.solicitante}</td>
        <td className="p-2 text-center">
          {isBaja ? (
            <button onClick={() => handleReactivarSingle(it)} className="text-green-600 hover:text-green-800 p-1 rounded hover:bg-green-50" title="Reactivar" data-testid={`row-reactivar-${idx}`}>
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
          ) : (
            <button onClick={() => openSingleBaja(it)} className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50" title="Dar de Baja" data-testid={`row-baja-${idx}`}>
              <XCircle className="w-3.5 h-3.5" />
            </button>
          )}
        </td>
      </tr>
    );
  };

  const renderTableHead = (items, isGroupCheckbox) => (
    <tr className="text-[10px] uppercase text-muted-foreground">
      <th className="p-2 w-10">
        {isGroupCheckbox ? (
          <Checkbox
            checked={items.every(it => selected.has(itemKey(it, it._idx)))}
            onCheckedChange={() => {
              const keys = items.map(it => itemKey(it, it._idx));
              const allSelected = keys.every(k => selected.has(k));
              setSelected(prev => {
                const next = new Set(prev);
                keys.forEach(k => allSelected ? next.delete(k) : next.add(k));
                return next;
              });
            }}
          />
        ) : (
          <Checkbox checked={data.length > 0 && selected.size === data.length} onCheckedChange={selectAll} data-testid="check-all" />
        )}
      </th>
      <th className="p-2 w-16">Prio</th>
      <SortableTh col="requisicion">Requisicion</SortableTh>
      <SortableTh col="fecha" className="text-center">Fecha</SortableTh>
      <SortableTh col="ot_numero">OT</SortableTh>
      <SortableTh col="descripcion">Descripcion</SortableTh>
      <SortableTh col="cantidad_solicitada" className="text-right" justify="justify-end">Solic.</SortableTh>
      <SortableTh col="cantidad_comprada" className="text-right" justify="justify-end">Compr.</SortableTh>
      <SortableTh col="cantidad_pendiente" className="text-right" justify="justify-end">Pend.</SortableTh>
      <th className="p-2 text-center">Und</th>
      <SortableTh col="concepto">Familia</SortableTh>
      <SortableTh col="solicitante">Solicitante</SortableTh>
      <th className="p-2 text-center w-16">Accion</th>
    </tr>
  );

  const SortableTh = ({ col, children, className = '', justify = '' }) => (
    <th
      className={`p-2 cursor-pointer select-none hover:text-gray-900 transition-colors ${className}`}
      onClick={() => handleSort(col)}
      title="Ordenar por esta columna"
      data-testid={`th-sort-${col}`}
    >
      <span className={`inline-flex items-center gap-0.5 w-full ${justify}`}>
        {children}
        {sortCol === col
          ? (sortDir === 'asc' ? <ArrowUp className="w-3 h-3 text-indigo-600 shrink-0" /> : <ArrowDown className="w-3 h-3 text-indigo-600 shrink-0" />)
          : <ArrowUpDown className="w-3 h-3 opacity-30 shrink-0" />}
      </span>
    </th>
  );

  // Cmd+K: comandos contextuales
  const selCount = selected.size;
  useRegisterCmdKCommands(
    'pendientes-compra',
    'Pendientes de Compra',
    [
      {
        id: 'pc-select-all',
        label: selCount === data.length && data.length > 0 ? 'Deseleccionar todos' : 'Seleccionar todos',
        sublabel: `${data.length} items en la vista`,
        icon: 'CheckCircle',
        keywords: 'seleccionar todos marcar',
        run: () => selectAll(),
        enabled: data.length > 0,
      },
      {
        id: 'pc-clear-selection',
        label: 'Limpiar selección',
        icon: 'XCircle',
        keywords: 'limpiar deseleccionar quitar',
        run: () => setSelected(new Set()),
        enabled: selCount > 0,
      },
      {
        id: 'pc-crear-oc',
        label: `Crear OC con seleccionados (${selCount})`,
        sublabel: 'Lleva los items al formulario de OC',
        icon: 'ShoppingCart',
        keywords: 'crear oc orden compra generar',
        run: () => goToGenerarOC(),
        enabled: selCount > 0,
      },
      {
        id: 'pc-solicitar-cot',
        label: `Solicitar cotización (${selCount})`,
        sublabel: 'Pide cotización a proveedores',
        icon: 'Send',
        keywords: 'solicitar cotizacion proveedor',
        run: () => goToSolicitarCotizacion(),
        enabled: selCount > 0,
      },
      {
        id: 'pc-dar-baja',
        label: `Dar de baja seleccionados (${selCount})`,
        icon: 'Trash2',
        keywords: 'dar de baja eliminar descartar',
        run: () => openBajaDialog(),
        enabled: selCount > 0,
      },
      {
        id: 'pc-toggle-bajas',
        label: mostrarBajas ? 'Ocultar items dados de baja' : 'Mostrar items dados de baja',
        icon: 'Eye',
        keywords: 'mostrar ocultar bajas',
        run: () => setMostrarBajas(v => !v),
      },
      {
        id: 'pc-refresh',
        label: 'Refrescar listado',
        icon: 'RefreshCw',
        run: () => loadData(page),
      },
    ],
    [selCount, data.length, mostrarBajas, page],
  );

  return (
    <div className="space-y-4" data-testid="pendientes-compra">
      {/* KPI summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="shadow-sm" data-testid="kpi-total-pendientes">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-orange-50"><Package className="w-5 h-5 text-orange-600" /></div>
            <div>
              <p className="text-[11px] text-muted-foreground leading-tight">Items Pendientes</p>
              <p className="text-xl font-bold">{fmt(stats.total)}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm" data-testid="kpi-ots-afectadas">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-50"><AlertTriangle className="w-5 h-5 text-blue-600" /></div>
            <div>
              <p className="text-[11px] text-muted-foreground leading-tight">OTs Afectadas</p>
              <p className="text-xl font-bold">{stats.ots_afectadas}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm" data-testid="kpi-familias">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-violet-50"><FolderOpen className="w-5 h-5 text-violet-600" /></div>
            <div>
              <p className="text-[11px] text-muted-foreground leading-tight">Familias</p>
              <p className="text-xl font-bold">{familias.length}</p>
            </div>
          </CardContent>
        </Card>
        {selected.size > 0 && (
          <Card className="shadow-sm border-indigo-300 bg-indigo-50" data-testid="kpi-seleccionados">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-indigo-100"><ShoppingCart className="w-5 h-5 text-indigo-600" /></div>
              <div>
                <p className="text-[11px] text-muted-foreground leading-tight">Seleccionados</p>
                <p className="text-xl font-bold text-indigo-700">{selected.size}</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 text-xs font-medium text-gray-500">
          <Filter className="w-3.5 h-3.5" /> Filtros:
        </div>
        {/* Toggle Origen Web/Access/Todas */}
        <div className="flex items-center gap-1 text-xs border rounded-md overflow-hidden h-8" data-testid="pend-filter-origen">
          <span className="px-2 text-gray-500 bg-gray-50 h-full flex items-center">Origen:</span>
          <button
            onClick={() => setFilterOrigen('web')}
            data-testid="pend-origen-web"
            className={`px-2.5 h-full transition-colors ${filterOrigen === 'web' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            title="Solo items de requisiciones web (accionables)"
          >
            Web
          </button>
          <button
            onClick={() => setFilterOrigen('access')}
            data-testid="pend-origen-access"
            className={`px-2.5 h-full transition-colors ${filterOrigen === 'access' ? 'bg-slate-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            title="Items legacy de pedidos Access"
          >
            Access
          </button>
          <button
            onClick={() => setFilterOrigen('todas')}
            data-testid="pend-origen-todas"
            className={`px-2.5 h-full transition-colors ${filterOrigen === 'todas' ? 'bg-gray-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
          >
            Todas
          </button>
        </div>
        <Input placeholder="OT..." value={filterOt} onChange={(e) => setFilterOt(e.target.value)}
          className="h-8 w-20 text-xs" data-testid="pend-filter-ot" />
        <select value={filterFamilia} onChange={(e) => setFilterFamilia(e.target.value)}
          className="h-8 px-2 rounded-md border border-input text-xs max-w-[200px]" data-testid="pend-filter-familia">
          <option value="">Todas las Familias</option>
          {familias.map(f => <option key={f} value={f}>{f} ({stats.por_familia[f]})</option>)}
        </select>
        <div className="relative flex-1 min-w-[160px] max-w-xs">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <Input placeholder="Buscar descripcion..." value={search}
            onChange={(e) => handleSearch(e.target.value)}
            className="h-8 pl-7 text-xs" data-testid="pend-search" />
        </div>
        <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}
          className="h-8 px-2 rounded-md border border-input text-xs" data-testid="pend-group-by">
          <option value="familia">Agrupar por Familia</option>
          <option value="ot">Agrupar por OT</option>
          <option value="requisicion">Agrupar por Requisición</option>
          <option value="none">Sin agrupar</option>
        </select>
        <Button variant={mostrarBajas ? 'default' : 'outline'} size="sm" className="gap-1 text-xs"
          onClick={() => setMostrarBajas(!mostrarBajas)} data-testid="toggle-bajas">
          {mostrarBajas ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          {mostrarBajas ? 'Ocultar Bajas' : 'Ver Dados de Baja'}
        </Button>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => imprimirResumen('familia')} className="gap-1 text-xs" title="Resumen compacto agrupado por Familia" data-testid="btn-resumen-familia">
            <Printer className="w-3.5 h-3.5" /> Resumen × Familia
          </Button>
          <Button variant="outline" size="sm" onClick={() => imprimirResumen('ot')} className="gap-1 text-xs" title="Resumen compacto agrupado por OT" data-testid="btn-resumen-ot">
            <Printer className="w-3.5 h-3.5" /> Resumen × OT
          </Button>
          <Button variant="outline" size="sm" onClick={imprimirPendientes} className="gap-1 text-xs" title="Imprimir listado" data-testid="btn-print-pendientes">
            <Printer className="w-3.5 h-3.5" /> Imprimir
          </Button>
          <Button variant="outline" size="sm" onClick={exportarPendientesExcel} className="gap-1 text-xs text-emerald-700 border-emerald-300 hover:bg-emerald-50" title="Exportar a Excel" data-testid="btn-excel-pendientes">
            <FileSpreadsheet className="w-3.5 h-3.5" /> Excel
          </Button>
        </div>
      </div>

      {/* Action bar - always visible */}
      <div className="flex flex-wrap items-center gap-2 p-3 bg-gray-50 rounded-lg border" data-testid="action-bar">
        <span className="text-xs text-muted-foreground">
          {selected.size > 0 ? <><strong className="text-gray-900">{selected.size}</strong> items seleccionados</> : 'Seleccione items con los checkboxes para realizar acciones'}
        </span>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {selBaja > 0 && (
            <Button size="sm" variant="outline" onClick={handleReactivar} className="gap-1.5 text-xs border-green-300 text-green-700 hover:bg-green-50" data-testid="btn-reactivar">
              <RotateCcw className="w-3.5 h-3.5" /> Reactivar ({selBaja})
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={openAsignarBatch} disabled={selActive === 0}
            className="gap-1.5 text-xs border-amber-300 text-amber-700 hover:bg-amber-50 disabled:opacity-40" title="Vincular todos los ítems seleccionados a un mismo material del maestro" data-testid="btn-asociar-material-batch">
            <FolderOpen className="w-3.5 h-3.5" /> Asociar material {selActive > 0 ? `(${selActive})` : ''}
          </Button>
          <Button size="sm" variant="outline" onClick={openBajaDialog} disabled={selActive === 0}
            className="gap-1.5 text-xs border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-40" data-testid="btn-dar-de-baja">
            <XCircle className="w-3.5 h-3.5" /> Dar de Baja {selActive > 0 ? `(${selActive})` : ''}
          </Button>
          <Button size="sm" onClick={goToGenerarOC} disabled={selActive === 0}
            className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-xs disabled:opacity-40" data-testid="btn-enviar-oc">
            <ShoppingCart className="w-4 h-4" /> Enviar a OC {selActive > 0 ? `(${selActive})` : ''}
          </Button>
          <Button size="sm" onClick={goToSolicitarCotizacion} disabled={selActive === 0}
            className="gap-1.5 bg-[#003366] hover:bg-[#002244] text-xs disabled:opacity-40" data-testid="btn-solicitar-cotizacion">
            <Send className="w-4 h-4" /> Cotizar {selActive > 0 ? `(${selActive})` : ''}
          </Button>
        </div>
      </div>

      {/* Dar de Baja Dialog */}
      <Dialog open={bajaOpen} onOpenChange={(open) => { setBajaOpen(open); if (!open) setSingleBajaItem(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-600" /> Dar de Baja {singleBajaItem ? '(1 item)' : `(${selActive} items)`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium mb-1 block">Motivo</label>
              <select value={bajaMotivo} onChange={(e) => setBajaMotivo(e.target.value)}
                className="w-full h-9 px-3 rounded-md border border-input text-sm" data-testid="baja-motivo">
                {MOTIVOS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Observacion (opcional)</label>
              <Input value={bajaObs} onChange={(e) => setBajaObs(e.target.value)}
                placeholder="Detalle adicional..." className="text-sm" data-testid="baja-obs" />
            </div>
            <p className="text-xs text-muted-foreground">Los items dados de baja no apareceran en la lista de pendientes. Puede reactivarlos usando el filtro "Ver Dados de Baja".</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBajaOpen(false)}>Cancelar</Button>
            <Button onClick={handleDarDeBajaConfirm} disabled={bajaProcesando} className="bg-red-600 hover:bg-red-700 gap-1.5" data-testid="baja-confirmar">
              {bajaProcesando ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
              Confirmar Baja
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 339: Asociar Familia a material sin clasificar · Iter 359: lote */}
      <Dialog open={!!asignarItem} onOpenChange={(open) => { if (!open) cerrarAsignar(); }}>
        <DialogContent className="sm:max-w-lg" data-testid="asignar-familia-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderOpen className="w-5 h-5 text-amber-600" /> {esBatch ? `Asociar material a ${asignarBatch.length} ítems` : 'Asociar Familia al Material'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {esBatch ? (
              <div className="text-sm bg-gray-50 border rounded-md p-2 max-h-32 overflow-y-auto divide-y" data-testid="asignar-batch-lista">
                {asignarBatch.map((it) => (
                  <div key={`${it.origen}-${it.requisicion}-${it.item_num}`} className="py-1 flex items-center gap-2">
                    <span className="font-mono text-[10px] text-gray-500 whitespace-nowrap">{it.origen === 'access' ? 'ACC' : 'WEB'}-{it.requisicion} · {it.item_num}</span>
                    <span className="truncate text-xs" title={it.descripcion}>{it.descripcion}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm bg-gray-50 border rounded-md p-2">
                <p className="font-mono text-xs text-gray-500">
                  {asignarItem?.codigo || asignarItem?.material_codigo || `${asignarItem?.origen === 'access' ? 'ACC' : 'WEB'}-${asignarItem?.requisicion} · ítem ${asignarItem?.item_num} (texto libre)`}
                </p>
                <p className="font-medium">{asignarItem?.descripcion}</p>
              </div>
            )}
            {!modoVincular ? (
              <div>
                <label className="text-sm font-medium mb-1 block">Subfamilia * (auto-asigna familia y cuenta de gasto)</label>
                <SearchableCombobox
                  items={subfamilias.map((sf) => ({
                    value: sf.codpf,
                    label: sf.subfamilia,
                    subLabel: `${sf.concepto} / ${sf.familia}`,
                  }))}
                  value={asignarCodpf}
                  onChange={(item) => setAsignarCodpf(item ? String(item.value) : '')}
                  placeholder="Buscar subfamilia…"
                  emptyMessage="Sin subfamilias"
                  testid="combobox-asignar-familia"
                />
                <p className="text-xs text-muted-foreground mt-2">El material quedará clasificado en el Maestro de Materiales y todos sus pedidos pendientes tomarán esta familia.</p>
              </div>
            ) : (
              <div className="space-y-2">
                <label className="text-sm font-medium block">Buscar material en el maestro *</label>
                <Input
                  value={matSearch}
                  onChange={(e) => { setMatSearch(e.target.value); setMatSelected(null); }}
                  placeholder="Descripción, código, familia o subfamilia… (ej: curva inox 316)"
                  data-testid="input-buscar-material"
                />
                <p className="text-[11px] text-muted-foreground">Puedes combinar palabras: todas deben calzar (descripción, familia o subfamilia).</p>
                {matSearching && <p className="text-xs text-muted-foreground">Buscando…</p>}
                {!matSelected && matResults.length > 0 && (
                  <>
                    <p className="text-[11px] text-muted-foreground" data-testid="mat-resultados-count">
                      {matTotal > matResults.length ? `${matTotal} coincidencias · mostrando ${matResults.length} — agrega palabras para afinar` : `${matResults.length} coincidencia(s)`}
                    </p>
                    <div className="border rounded-md max-h-44 overflow-y-auto divide-y" data-testid="lista-materiales-resultados">
                      {matResults.map((m) => (
                        <button
                          key={m.codigo}
                          onClick={() => { setMatSelected(m); setMatResults([]); setSubfamSolo(''); }}
                          className="w-full text-left px-2 py-1.5 text-xs hover:bg-amber-50 transition-colors"
                          data-testid={`mat-resultado-${m.codigo}`}
                        >
                          <span className="font-mono text-gray-500">{m.codigo}</span>{' '}
                          <span className="font-medium">{m.descripcion2 || m.descripcion}</span>
                          <span className="block text-[10px] text-muted-foreground">
                            {m.concepto ? `${m.concepto} / ${m.familia_nombre || ''}${m.subfamilia_nombre ? ` / ${m.subfamilia_nombre}` : ''}` : '⚠ sin familia asignada'}
                          </span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
                {matSearch && !matSearching && !matSelected && matResults.length === 0 && (
                  <p className="text-xs text-muted-foreground">Sin resultados. Si no existe, créalo en Maestro de Materiales.</p>
                )}
                {matSelected && (
                  <div className="text-xs bg-emerald-50 border border-emerald-200 rounded-md p-2" data-testid="material-seleccionado">
                    <span className="font-mono text-gray-500">{matSelected.codigo}</span>{' '}
                    <span className="font-medium">{matSelected.descripcion}</span>
                    <span className="block text-emerald-700">{matSelected.concepto ? `Familia: ${matSelected.concepto}` : 'Este material no tiene familia — asígnala abajo'}</span>
                  </div>
                )}
                {matSelected && !matSelected.concepto && (
                  <SearchableCombobox
                    items={subfamilias.map((sf) => ({
                      value: sf.codpf,
                      label: sf.subfamilia,
                      subLabel: `${sf.concepto} / ${sf.familia}`,
                    }))}
                    value={asignarCodpf}
                    onChange={(item) => setAsignarCodpf(item ? String(item.value) : '')}
                    placeholder="Buscar subfamilia para este material…"
                    emptyMessage="Sin subfamilias"
                    testid="combobox-asignar-familia"
                  />
                )}
                <p className="text-xs text-muted-foreground">{esBatch ? 'Todos los ítems seleccionados quedarán vinculados a este material y heredarán su familia.' : 'El ítem quedará vinculado al material del maestro y heredará su familia.'}</p>
                <div className="rounded-md border border-dashed border-amber-300 bg-amber-50/40 p-2 space-y-1.5" data-testid="seccion-subfamilia-solo">
                  <p className="text-xs font-medium text-amber-800">¿No existe el material exacto? Clasifica solo por subfamilia</p>
                  <SearchableCombobox
                    items={subfamilias.map((sf) => ({
                      value: sf.codpf,
                      label: sf.subfamilia,
                      subLabel: `${sf.concepto} / ${sf.familia}`,
                    }))}
                    value={subfamSolo}
                    onChange={(item) => { setSubfamSolo(item ? String(item.value) : ''); if (item) { setMatSelected(null); setMatResults([]); } }}
                    placeholder="Buscar subfamilia (ej: Curvas, Fitting Ac. Inox.)…"
                    emptyMessage="Sin subfamilias"
                    testid="combobox-subfamilia-solo"
                  />
                  {subfamSolo && !matSelected && (
                    <p className="text-[11px] text-amber-800" data-testid="hint-subfamilia-solo">
                      {esBatch ? `Los ${asignarBatch.length} ítems` : 'El ítem'} quedará(n) clasificado(s) por subfamilia (hereda familia y cuenta de gasto), sin código de material.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={cerrarAsignar} disabled={asignarLoading}>Cancelar</Button>
            <Button
              onClick={handleAsignarFamilia}
              disabled={asignarLoading || (!modoVincular ? !asignarCodpf : !(matSelected || subfamSolo))}
              className="bg-amber-600 hover:bg-amber-700 gap-1.5"
              data-testid="asignar-familia-confirmar"
            >
              {asignarLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FolderOpen className="w-4 h-4" />}
              {!modoVincular ? 'Asociar Familia'
                : (!matSelected && subfamSolo) ? (esBatch ? `Asociar subfamilia (${asignarBatch.length})` : 'Asociar subfamilia')
                  : (esBatch ? `Vincular ${asignarBatch.length} ítems` : 'Vincular Material')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Table */}
      {loading ? (
        <TableSkeleton rows={8} cols={6} />
      ) : data.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="font-medium">No hay items pendientes de compra</p>
          <p className="text-xs">Todos los items aprobados ya tienen OC asignada o fueron dados de baja</p>
        </div>
      ) : groupBy !== 'none' && groupedData ? (
        <div className="space-y-2">
          {groupedData.map(([groupLabel, items]) => (
            <div key={groupLabel} className="border rounded-lg overflow-hidden">
              <button
                className="w-full flex items-center gap-2 p-3 bg-gray-50 hover:bg-gray-100 text-sm font-medium transition-colors text-left"
                onClick={() => toggleGroup(groupLabel)}
                data-testid={`group-${groupLabel}`}
              >
                {expandedGroups.has(groupLabel) ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                <span>{groupLabel}</span>
                <span className="ml-auto text-xs text-muted-foreground bg-gray-200 px-2 py-0.5 rounded-full">{items.length} items</span>
              </button>
              {expandedGroups.has(groupLabel) && (
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead className="bg-gray-50 border-t">{renderTableHead(items, true)}</thead>
                    <tbody>{items.map(it => renderRow(it, it._idx))}</tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <div className="overflow-x-auto max-h-[65vh]">
            <table className="w-full text-left">
              <thead className="bg-gray-50 sticky top-0 z-10">{renderTableHead(sortedData, false)}</thead>
              <tbody>{sortedData.map((it) => renderRow(it, it._idx))}</tbody>
            </table>
          </div>
          <div className="flex items-center justify-between p-2 bg-gray-50 border-t text-xs text-muted-foreground">
            <span>Mostrando {page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, stats.total)} de {fmt(stats.total)} items</span>
            <div className="flex items-center gap-2">
              {selected.size > 0 && <span className="font-medium text-indigo-700">{selected.size} seleccionados</span>}
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => { setPage(p => p - 1); loadData(page - 1); }} data-testid="pend-prev-page">Anterior</Button>
              <span className="font-medium">Pag {page + 1} / {Math.ceil(stats.total / PAGE_SIZE) || 1}</span>
              <Button variant="outline" size="sm" disabled={(page + 1) * PAGE_SIZE >= stats.total} onClick={() => { setPage(p => p + 1); loadData(page + 1); }} data-testid="pend-next-page">Siguiente</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
